import { TrackedLink } from "@faire/retailer-visitor-shared/components/TrackedLink/index";
import { InsightCardContext } from "@faire/retailer-visitor-shared/features/InsightsFeed/InsightCardContext";
import { InsightCardProductImage } from "@faire/retailer-visitor-shared/features/InsightsFeed/InsightCardProductImage";
import { useProductTileClickImpressions } from "@faire/retailer-visitor-shared/hooks/ProductTileImpression/useProductTileClickImpressions";
import { useProductTileViewImpression } from "@faire/retailer-visitor-shared/hooks/ProductTileImpression/useProductTileViewImpression";
import { useEffectEvent } from "@faire/web--source/ui/hooks/useEffectEvent";
import { TransparencyOverlay } from "@faire/web--source/ui/TransparencyOverlay";
import { IInsightTile } from "@faire/web-api--source/indigofair/data/IInsightTile";
import productPage from "@faire/web-api--source/routes/www/product/productToken";
import React, { useContext } from "react";
import styled from "styled-components";

interface InsightCardProductProps {
  brandToken: string;
  product: IInsightTile.IProductData;
  asCarouselTile: boolean;
  shouldShowVerticalImagery: boolean;
  isCompact: boolean;
}

export const InsightCardProduct: React.FC<InsightCardProductProps> = ({
  brandToken,
  product,
  asCarouselTile,
  shouldShowVerticalImagery,
  isCompact,
}) => {
  const productToken = product.product_token ?? "";
  const impressionId = product.impression_id;

  const { onProductClick } = useContext(InsightCardContext);
  const { recordProductTileClickEvent } = useProductTileClickImpressions(
    {
      token: productToken,
      bestImage: product.image,
      impressionId,
    },
    brandToken
  );

  const { ref: trackProductTileViewRef, ...restTrackProductTileViewProps } =
    useProductTileViewImpression({
      productToken,
      impressionId,
    });

  const internalOnProductClick = useEffectEvent(() => {
    recordProductTileClickEvent();
    onProductClick?.(productToken);
  });

  return (
    <ProductLink
      innerRef={trackProductTileViewRef}
      {...restTrackProductTileViewProps}
      token={product.product_token ?? ""}
      to={productPage(product.product_token ?? "")}
      onClick={internalOnProductClick}
      data-test-id={`insightTile-product-link-${product.product_token}`}
    >
      <InsightCardProductImage
        image={product.image}
        asCarouselTile={asCarouselTile}
        shouldShowVerticalImagery={shouldShowVerticalImagery}
        isCompact={isCompact}
      />
      <TransparencyOverlay />
    </ProductLink>
  );
};

const ProductLink = styled(TrackedLink)`
  position: relative;
  flex: 1;
  margin: 2px;

  &:focus-visible {
    outline: 2px solid -webkit-focus-ring-color;
  }
`;
